import { debounce } from 'lodash';
import { MuiTelInput } from 'mui-tel-input';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getContractTypeLabel } from '../../../../constants/labels/user';
import { contractTypeEnum } from '../../../../constants/user';
import { useCreateUserMutation, useUpdateUserMutation } from '../../../../hooks/mutations/useUserMutation';
import { useGeocodeQuery } from '../../../../hooks/queries/useGeocodingQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Job } from '../../../../services/apiSLTP/jobs';
import { Profile } from '../../../../services/apiSLTP/profiles';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import {
    crudStatus,
    getPriceTypeInfos,
    getSpecialUserTypeLabel,
    priceTypeEnum,
    specialUserTypeEnum,
    systemConfigKeyEnum,
} from '../../../../utils/enums';
import CustomDateInput from '../../../CustomDateInput';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    user: User | null;
    jobs: Job[];
    profiles: Profile[];
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateUserModal: React.FC<ModalProps> = ({ user, showEditModal, setShowEditModal, jobs, profiles, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { user: loggedUser } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [firstName, setFirstName] = useState(user?.firstName || '');
    const [lastName, setLastName] = useState(user?.lastName || '');
    const [codeProC, setCodeProC] = useState(user?.codeProC || '');
    const [address, setAddress] = useState(user?.address || '');
    const [debouncedAddress, setDebouncedAddress] = useState(user?.address || '');
    const [latitude, setLatitude] = useState(user?.latitude?.toString() || '');
    const [longitude, setLongitude] = useState(user?.longitude?.toString() || '');
    const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
    const [username, setUsername] = useState(user?.username || '');
    const [password, setPassword] = useState('');
    const [payrollNumber, setPayrollNumber] = useState(user?.payrollNumber || '');
    const [isActive, setIsActive] = useState(user?.crudStatus ? user.crudStatus === crudStatus.OK : true);
    const [isSpecial, setIsSpecial] = useState(user?.isSpecial || false);
    const [isSST, setIsSST] = useState(user?.isSST || false);
    const [color, setColor] = useState(user?.color || '#F5F5F5');
    const [selectedJob, setSelectedJob] = useState({ label: '', value: '' });
    const [selectedProfile, setSelectedProfile] = useState({ label: '', value: '' });
    const [selectedContractType, setSelectedContractType] = useState<OptionType | null>(
        user?.contractType ? { label: capitalizeFirstLetter(getContractTypeLabel(user?.contractType)), value: (user?.contractType).toString() } : null
    );
    const [arrivalDate, setArrivalDate] = useState(user?.arrivalDate ? new Date(user.arrivalDate) : undefined);
    const [departureDate, setDepartureDate] = useState(user?.departureDate ? new Date(user.departureDate) : undefined);
    const [price, setPrice] = useState(typeof user?.price === 'number' ? user?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        user?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeInfos(user?.priceType))),
                  value: (user?.priceType).toString(),
              }
            : null
    );
    const [specialUserType, setSpecialUserType] = useState<OptionType | null>(
        user?.specialType
            ? {
                  label: capitalizeFirstLetter(t(getSpecialUserTypeLabel(user?.specialType))),
                  value: (user?.specialType).toString(),
              }
            : null
    );

    const { data: coords, isLoading: isGeocoding } = useGeocodeQuery({
        address: debouncedAddress,
    });

    useEffect(() => {
        const job = jobs.find((j) => j._id === user?.jobId?._id);
        if (job) setSelectedJob({ label: job.designation, value: job._id });

        const profile = profiles.find((p) => p._id === user?.profileId?._id);
        if (profile) setSelectedProfile({ label: profile.type.toUpperCase(), value: profile._id });
    }, [user, jobs, profiles]);

    const { mutate: mutateUser } = useUpdateUserMutation({ onSuccess: refetch });
    const { mutate: addUser } = useCreateUserMutation({ onSuccess: refetch });

    const handleSubmit = () => {
        const userData = {
            firstName,
            lastName,
            codeProC,
            address,
            phoneNumber,
            username,
            crudStatus: isActive ? crudStatus.OK : crudStatus.INACTIVE,
            jobId: selectedJob.value,
            profileId: selectedProfile.value,
            isSpecial,
            isSST,
            color,
            arrivalDate: arrivalDate?.toString(),
            departureDate: departureDate?.toString(),
            priceType: priceType ? parseInt(priceType.value) : null,
            price: parseFloat(price),
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
            specialType: specialUserType ? parseInt(specialUserType.value) : null,
            contractType: selectedContractType ? parseInt(selectedContractType.value) : null,
            payrollNumber,
        };

        if (isEditMode) {
            mutateUser({ userId: user?._id || '', data: userData });
        } else {
            const newUser = { ...userData, password };
            addUser(newUser);
        }
    };

    const handleSSTChange = (checked: boolean) => setIsSST(checked);
    const handleSpecialChange = (checked: boolean) => setIsSpecial(checked);
    const handleColorChange = (newColor: string) => setColor(newColor);

    const isFormValid = () => {
        return firstName && lastName && codeProC && username && selectedJob && selectedProfile && (isEditMode || password);
    };

    const debouncedSetAddress = useCallback(
        debounce((value: string) => {
            setDebouncedAddress(value);
        }, 500),
        []
    );

    useEffect(() => {
        if (coords) {
            setLatitude(coords.lat.toString());
            setLongitude(coords.lon.toString());
        }
    }, [coords]);

    useEffect(() => {
        debouncedSetAddress(address);
    }, [address, debouncedSetAddress]);

    return (
        <ModalBody
            title={t('user')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            disabledOk={!isFormValid()}
            handleClose={() => setShowEditModal(false)}
            handleOk={handleSubmit}
        >
            <CustomTextInput value={firstName} label={t('firstName')} onChange={setFirstName} required />
            <CustomTextInput value={lastName} label={t('lastName')} onChange={setLastName} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={setCodeProC} required />
            <ToggleLine checked={isActive} onChange={setIsActive} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={address} label={t('address')} onChange={setAddress} />
            <CustomTextInput value={latitude} label={t('latitude')} onChange={(text) => setLatitude(text)} type="number" />
            <CustomTextInput value={longitude} label={t('longitude')} onChange={(text) => setLongitude(text)} type="number" />
            <div className="pad-l-5px pad-r-5px">
                <MuiTelInput
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    label={capitalizeFirstLetter(t('mobile'))}
                    defaultCountry="FR"
                    preferredCountries={['FR', 'BE', 'CH', 'LU']}
                    fullWidth
                    size="small"
                    langOfCountryName={t('locale')}
                />
            </div>
            <CustomTextInput value={username} label={t('username')} onChange={setUsername} required />
            {!isEditMode && <CustomTextInput value={password} label={t('password')} onChange={setPassword} required />}
            <DropDown
                items={jobs.map((j) => ({ label: j.designation, value: j._id }))}
                selectedItem={selectedJob}
                handleChange={(e, v) => setSelectedJob(v || { label: '', value: '' })}
                inputLabel={t('job')}
                required
            />
            <DropDown
                items={profiles.sort((a, b) => a.permsLevel - b.permsLevel).map((p) => ({ label: p.type.toUpperCase(), value: p._id }))}
                selectedItem={selectedProfile}
                handleChange={(e, v) => setSelectedProfile(v || { label: '', value: '' })}
                inputLabel={t('profile')}
                required
                sortItems={false}
            />
            <DropDown
                items={Object.values(contractTypeEnum).map((type) => ({
                    label: getContractTypeLabel(type).toUpperCase(),
                    value: type.toString(),
                }))}
                selectedItem={selectedContractType}
                handleChange={(e, v) => setSelectedContractType(v)}
                inputLabel={t('contractType')}
            />
            <CustomTextInput value={payrollNumber} onChange={setPayrollNumber} label={t('payrollNumber')} />
            <CustomDateInput
                label={t('arrivalDate')}
                onChange={(value) => (value ? setArrivalDate(new Date(value?.toISOString())) : null)}
                defaultDate={arrivalDate}
            />
            <CustomDateInput
                label={t('departureDate')}
                onChange={(value) => (value ? setDepartureDate(new Date(value?.toISOString())) : null)}
                defaultDate={departureDate}
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('pricing')}
                disableClearable={false}
            />
            <ToggleLine checked={isSST} onChange={handleSSTChange} title={t('sst').toUpperCase()} />
            <ToggleLine checked={isSpecial} onChange={handleSpecialChange} title={capitalizeFirstLetter(t('special'))} />
            {isSpecial && (
                <DropDown
                    items={Object.values(specialUserTypeEnum).map((type) => ({
                        label: capitalizeFirstLetter(t(getSpecialUserTypeLabel(type))),
                        value: type.toString(),
                    }))}
                    selectedItem={specialUserType}
                    handleChange={(e, value) => {
                        setSpecialUserType(value);
                    }}
                    inputLabel={t('specialUserType')}
                    disableClearable={false}
                />
            )}
            <CustomTextInput label={capitalizeFirstLetter(t('color'))} type="color" value={color} onChange={handleColorChange} />
        </ModalBody>
    );
};

export default UpdateUserModal;
